<template>
  <div v-show="isActive" class="select-tool-container" id="selectID">
    <!--Se cambia nombre de herramienta segun observacion de SENAPRED 06/2026 ("Le herramienta no es de selección, sino de consulta, por lo que debería llamarse "Consulta"")-->
    <MapToolHeader 
      title="Consulta" 
      :isHidden.sync="isHidden" 
      headerId="selectIDheader"
      @close="hidePanel"
    />

    <!-- Body -->
    <div class="tool-body" v-show="!isHidden">
      <div class="control-group">
        <!--observacion de SENAPRED 06/2026 ('- La bajada debería ser "Consultar información mediante:"')-->
        <label for="select-mode">Consultar información mediante:</label>
        <select id="select-mode" v-model="mode" class="custom-select">
          <option v-for="(item, index) in options" :key="index" :value="item">
            {{ item.name }}
          </option>
        </select>
      </div>

      <div class="actions-bar">
        <button class="btn-clean" @click="cleanSelection">
          Limpiar <font-awesome-icon icon="trash-alt" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { draggableDiv } from '@/components/mixin/draggableDiv.js'
import { vuex } from '@/components/mixin/vuex.js'
import { selectService } from '@/services/map/selectService.js'
import { mapService } from '@/services/map/mapService.js'
import MapToolHeader from '@/components/common/MapToolHeader.vue'

export default {
  name: 'SelectTool',
  mixins: [vuex, draggableDiv],
  components: {
    MapToolHeader
  },
  props: {
    isFixed: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      mode: { name: 'Punto', code: 'point', drawType: 'Point' },
      isHidden: false
    }
  },
  computed: {
    isActive() {
      if (this.isFixed) {
        return this.isMobile
      }
      return this.mapToolValues('select').active && !this.isMobile
    },
    options() {
      //observacion de SENAPRED 06/2026 ("- Las opciones deberían ser Punto, Polígono o Círculo")
      const opts = [
        { name: 'Punto', code: 'point', drawType: 'Point' }
      ]
      if (!this.isMobile) {
        opts.push({ name: 'Polígono', code: 'area', drawType: 'Polygon' })
        opts.push({ name: 'Círculo', code: 'area', drawType: 'Circle' })
      }
      return opts
    }
  },
  watch: {
    isActive(val) {
      if (val) {
        if (selectService.isListening()) {
          // Service already running — panel was soft-closed; preserve popup, sync layers
          selectService.setActiveLayers(this.activeLayers);
          return;
        }
        this.initTool();
      }
      // No else — panel hide does NOT stop the service
    },
    mode(newMode) {
      if (this.isActive) {
        this.applyMode(newMode);
      }
    },
    activeLayers(newLayers) {
      const serviceRunning = selectService.isListening();

      if (newLayers.length === 0) {
        if (this.isActive || serviceRunning) {
          console.log('[Select] All layers removed — deactivating Point Selection')
          this.fullTeardown();
        }
        return;
      }

      if (serviceRunning) {
        // Layers changed while service already active — just sync
        selectService.setActiveLayers(newLayers);
      } else {
        // Implicit activation — start point selection without opening panel
        this.initTool();
      }
    }
  },
  mounted() {
    this.makeDraggable("selectID");
    if (this.isActive) {
      this.initTool();
    }
  },
  beforeDestroy() {
    this.disableTool();
  },
  methods: {
    initTool() {
      const map = mapService.get();
      if (!map) return;

      selectService.init(map);
      selectService.setActiveLayers(this.activeLayers);
      this.applyMode(this.mode);
    },
    disableTool() {
      selectService.disable();
    },
    applyMode(modeItem) {
      if (modeItem.code === 'point') {
        selectService.enablePointSelection();
      } else {
        selectService.enableAreaSelection(modeItem.drawType);
      }
    },
    cleanSelection() {
      selectService.clearResults();
    },
    fullTeardown() {
      selectService.disable();
      this.mode = { name: 'Punto', code: 'point', drawType: 'Point' };
      this.$store.commit('setMapToolSelection', { tool: 'select', active: false });
    },
    hidePanel() {
      if (this.mode.code !== 'point') {
        this.fullTeardown();
        return;
      }
      // Point mode: soft close — panel hides, listener + popup + cursor persist
      this.$store.commit('setMapToolSelection', { tool: 'select', active: false });
    }
  }
}
</script>

<style scoped lang="scss">
.select-tool-container {
  position: absolute;
  top: 12em;
  left: 78vw;
  width: 250px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-family: 'Inter', sans-serif;
  overflow: hidden;
  z-index: 1000;
}

.tool-body {
  padding: 15px;
  background: #f8fafc;
}

.control-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #334155;
  letter-spacing: 0.3px;
}

.custom-select {
  width: 100%;
  padding: 8px 10px;
  border-radius: 4px;
  border: 1px solid #cbd5e1;
  background: white;
  font-size: 0.9rem;
  color: #1e293b;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #3282b8;
    box-shadow: 0 0 0 2px rgba(50, 130, 184, 0.1);
  }
}

.info-msg {
  margin-top: 10px;
  color: #64748b;
}

.actions-bar {
  margin-top: 15px;
  display: flex;
  justify-content: center;
}

.btn-clean {
  background-color: #e2e8f0;
  color: #475569;
  border: 1px solid #cbd5e1;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background-color: #cbd5e1;
    color: #1e293b;
  }
}
</style>
